import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Collapse, IconButton, InputAdornment, TablePagination, Typography, makeStyles } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Close, SearchSharp } from '@material-ui/icons';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  PaginationState,
  SortingState,
  Updater,
  VisibilityState,
  flexRender,
  useReactTable,
} from '@tanstack/react-table';

import { FilterIcon } from '../../assets';
import { EmptyBoxIcon } from '../../assets/icons/EmptyBoxIcon';
import { colors } from '../../assets/theme/palette';
import { Button } from '../../components/button/Button';
import { Checkbox } from '../../components/checkbox/Checkbox';
import { NewDropdownMenu } from '../../components/dropdownMenu/newDropdownMenu/NewDropdownMenu';
import { Input } from '../../components/input/Input';
import { Paper } from '../../components/paper/Paper';
import { useEllipsis } from '../useEllipsis/useEllipsis';
import { Cell, Row, SortLabel } from './NewDataTable.styles';
import { defaultPerPageOptions } from './NewDataTable.utils';
import { useColumnVisibility } from './useColumnVisibility';
import { useExportData } from './useExportData';
import { useKebabMenu } from './useKebabMenu';
import { OasisDataTableProps } from './useOasisDataTable.types';

const useOasisTablePaginationStyles = makeStyles({
  root: {
    border: 'none',
    padding: 0,
  },
  toolbar: {
    flex: 1,
    padding: '0 16px',
  },
  spacer: {
    flex: 'initial',
  },
  caption: {
    '&:nth-of-type(2)': {
      order: -1,
      marginRight: 20,
      color: colors.grey140,
      fontWeight: 'bold',
    },
    '&:nth-of-type(1)': {
      flex: 1,
      textAlign: 'right',
      color: colors.grey140,
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    },
  },
});

// ─── Search-field dropdown (CID-6934) ────────────────────────────────────────
const SEARCH_FIELD_TRIGGER_STYLE: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: colors.primary.main,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '0 4px 0 8px',
  font: 'inherit',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  // minWidth:0 lets the trigger shrink inside the flex adornment so the label
  // region (the inner span) can truncate. The width cap and clipping live on
  // that span, not here — keeping the button's own box unchanged.
  minWidth: 0,
};

// Width cap for the label region inside the trigger. Fluid: ~96px floor on
// small phones (iPhone 12/SE), scaling with the viewport up to 200px on
// desktop, so a long label (e.g. "Name, Email, Company, ID, Phone") truncates
// to an ellipsis + tooltip instead of pushing past the input on narrow screens.
const SEARCH_FIELD_LABEL_STYLE: React.CSSProperties = {
  minWidth: 0,
  maxWidth: 'clamp(96px, 30vw, 200px)',
  overflow: 'hidden',
};

// Temporarily hidden — examples (e.g. "Abc", "123") removed from the dropdown.
// Restore the helper + styles below and the `onRenderName` block in
// `searchFieldMenuItems` to bring them back.
//
// const SEARCH_FIELD_MENU_ITEM_STYLE: React.CSSProperties = {
//   display: 'flex',
//   alignItems: 'baseline',
//   justifyContent: 'space-between',
//   gap: 24,
//   width: '100%',
// };
//
// const SEARCH_FIELD_MENU_ITEM_EXAMPLE_STYLE: React.CSSProperties = {
//   color: colors.grey120,
//   fontWeight: 400,
//   fontSize: '0.875em',
// };
//
// const SEARCH_FIELD_TYPE_EXAMPLES: Record<NonNullable<SearchFieldOption['type']>, string> = {
//   number: '123',
//   phone: '555-123-4567',
//   email: 'name@example.com',
//   text: 'Abc',
// };
//
// function resolveSearchFieldExample(opt: SearchFieldOption): string | undefined {
//   return opt.example ?? (opt.type ? SEARCH_FIELD_TYPE_EXAMPLES[opt.type] : undefined);
// }

export function useOasisDataTable<TData>({
  loading = false,
  searchable = false,
  searchPlaceholder,
  pageSizeOptions,
  rowCount,
  hidePagination = false,
  kebabMenuItems,
  checkboxSelection,
  rowStyle = () => ({}),
  onRowClick,
  handleKebabMenuMenuItemClick,
  onPageChange,
  onPerPageChange,
  onSearchInputChange,
  onClearSearchInput,
  onClearFilters,
  externalAction,
  sorting,
  onSortingChange,
  enableSortingRemoval,
  pageSize,
  pageIndex,
  searchText,
  searchFields,
  searchField,
  onSearchFieldChange,
  hasActiveFilters,
  filterPanel,
  actions,
  styles,
  RowComponent,
  ...tableOptions
}: OasisDataTableProps<TData>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    tableOptions.initialState?.columnVisibility ?? {},
  );

  const toggleFilterPanel = useCallback(() => {
    setFilterPanelOpen(prev => !prev);
  }, []);

  // Memoize table options
  // Only include pagination in state when values are explicitly provided (controlled mode)
  // Otherwise, let React Table manage pagination internally using initialState
  const controlledPagination =
    pageSize !== undefined && pageIndex !== undefined ? { pagination: { pageSize, pageIndex } } : {};

  const memoizedTableOptions = useMemo(
    () => ({
      ...tableOptions,
      state: {
        sorting,
        columnVisibility,
        ...controlledPagination,
      },
      onColumnVisibilityChange: setColumnVisibility,
      enableSorting: true,
      sortDescFirst: false,
      // Default: disabled for server-side tables so the cycle is asc↔desc only
      // (removing sort falls back to defaultSort, appearing broken). Pages that
      // handle the "no sort" state explicitly can opt into the 3-state cycle
      // (asc → desc → none) by passing enableSortingRemoval.
      enableSortingRemoval: enableSortingRemoval ?? !onSortingChange,
      manualSorting: !!onSortingChange, // Enable manual sorting if onSortingChange is provided
      manualPagination: !!(onPageChange || onPerPageChange), // Enable manual pagination if callbacks are provided
      onSortingChange: onSortingChange
        ? (updater: SortingState | ((old: SortingState) => SortingState)) => {
            // Handle both function updaters and direct values
            if (typeof updater === 'function') {
              // If it's a function updater, call it with current sorting state
              const newSorting = updater(sorting || []);
              onSortingChange(newSorting);
            } else {
              // If it's a direct value, pass it through
              onSortingChange(updater);
            }
          }
        : undefined,
      onPaginationChange:
        onPageChange || onPerPageChange
          ? (updater: Updater<PaginationState>) => {
              // NOTE: table.setPagination always passes a functional updater, so both values are
              // undefined here; useTablePagination reports the real page/size to the callbacks itself.
              const { pageIndex: newPageIndex, pageSize: newPageSize } = updater as PaginationState;
              if (newPageIndex !== pageIndex && onPageChange) {
                onPageChange(newPageIndex);
              }
              if (newPageSize !== pageSize && onPerPageChange) {
                onPerPageChange(newPageSize);
              }
            }
          : undefined,
    }),
    [
      tableOptions,
      sorting,
      columnVisibility,
      onSortingChange,
      enableSortingRemoval,
      pageSize,
      pageIndex,
      onPageChange,
      onPerPageChange,
    ],
  );

  const table = useReactTable<TData>(memoizedTableOptions);

  // Memoize table state values
  const allColumns = useMemo(() => table.getAllColumns(), [table]);

  // Pagination functionality (integrated from useTablePagination)
  const isShowAction = useMemo(() => externalAction?.visible ?? true, [externalAction]);

  const onChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      table.setPageIndex(newPage);

      if (onPageChange) {
        onPageChange(newPage);
      }
    },
    [onPageChange, table],
  );

  const onChangePerPage = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const perPage = Number(event.target.value);

      table.setPageIndex(0);
      table.setPageSize(perPage);

      if (onPerPageChange) {
        onPerPageChange(perPage);
      }
    },
    [onPerPageChange, table],
  );

  // Get total row count for pagination
  const totalRowCount = useMemo(() => {
    // For server-side pagination, use provided rowCount
    if (rowCount !== undefined) {
      return rowCount;
    }
    // Otherwise fall back to client-side calculation
    return table.getPrePaginationRowModel().rows.length;
  }, [rowCount, table]);

  const paginationClasses = useOasisTablePaginationStyles();

  const renderPagination = useCallback(
    () => (
      <TableRow>
        {isMobile && externalAction && isShowAction ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={externalAction.onClick}
              disabled={loading}
              style={{ color: colors.primary.main, fontSize: 12 }}
            >
              {externalAction.label}
            </Button>
          </div>
        ) : (
          ''
        )}
        {totalRowCount > 0 && (
          <TablePagination
            classes={paginationClasses}
            colSpan={allColumns.length}
            count={totalRowCount}
            page={table.getState().pagination.pageIndex}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangePerPage}
            rowsPerPageOptions={pageSizeOptions ?? defaultPerPageOptions}
            rowsPerPage={table.getState().pagination.pageSize}
          />
        )}
      </TableRow>
    ),
    [
      isMobile,
      externalAction,
      isShowAction,
      loading,
      paginationClasses,
      allColumns.length,
      totalRowCount,
      table,
      onChangePage,
      onChangePerPage,
      pageSizeOptions,
    ],
  );

  const { handleOpenColumnVisibility } = useColumnVisibility({
    columns: allColumns,
    columnVisibility,
    columnSorting: {}, // We don't need column sorting in the visibility menu
  });

  const { renderKebabMenu } = useKebabMenu({ kebabMenuItems, handleKebabMenuMenuItemClick });

  const { exportExcel, exportCsv, exportPdf } = useExportData({ table });

  // Memoize row click handler
  const handleRowClick = useCallback(
    (row: TData) => {
      if (!loading && onRowClick) {
        const selection = window.getSelection();

        if (selection && selection.toString().length > 0) {
          return;
        }

        onRowClick(row);
      }
    },
    [loading, onRowClick],
  );

  // Memoize checkbox handlers
  const handleCheckboxChange = useCallback(() => {
    // Add checkbox selection logic here
  }, []);

  const handleSearchInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSearchInputChange?.(event.target.value);
    },
    [onSearchInputChange],
  );

  const handleClearSearchInput = useCallback(() => {
    onClearSearchInput?.();
  }, [onClearSearchInput]);

  // ─── In-input field-scope dropdown (CID-6934) ─────────────────────────────
  const [searchFieldMenuAnchor, setSearchFieldMenuAnchor] = useState<HTMLElement | null>(null);

  const handleSelectSearchField = useCallback(
    (id: string) => {
      onSearchFieldChange?.(id);
      setSearchFieldMenuAnchor(null);
    },
    [onSearchFieldChange],
  );

  // Filter out options whose backing column is currently hidden via the
  // column-visibility menu. The default option (first entry, typically with
  // no `columnId`) is always kept so the dropdown never collapses to nothing.
  // `columnVisibility[id] === false` means hidden; anything else (true or
  // missing) means visible — matches tanstack's semantics.
  const visibleSearchFields = useMemo(() => {
    if (!searchFields || searchFields.length === 0) return searchFields;
    return searchFields.filter((opt, idx) => {
      if (idx === 0) return true;
      if (!opt.columnId) return true;
      return columnVisibility[opt.columnId] !== false;
    });
  }, [searchFields, columnVisibility]);

  const showSearchFieldDropdown = !!visibleSearchFields && visibleSearchFields.length >= 2;
  const selectedSearchField = showSearchFieldDropdown
    ? (visibleSearchFields.find(f => f.id === searchField) ?? visibleSearchFields[0])
    : undefined;
  const isNonDefaultScope = !!selectedSearchField && selectedSearchField.id !== visibleSearchFields?.[0].id;

  // If the currently selected scope was filtered out (its column got hidden),
  // notify the page so it can reset the URL `searchField` param back to default.
  // NOTE: this must NOT be gated on `showSearchFieldDropdown`. Hiding the column
  // of the only advanced scope collapses `visibleSearchFields` to just the
  // default entry (length 1), which turns the dropdown off — but the stale
  // `searchField` would otherwise stay in the URL and keep filtering by a
  // hidden column. We always reset to the first (default) entry, which is
  // guaranteed to survive the visibility filter.
  useEffect(() => {
    if (!searchField || !visibleSearchFields || visibleSearchFields.length === 0) return;
    const stillVisible = visibleSearchFields.some(f => f.id === searchField);
    if (!stillVisible) {
      onSearchFieldChange?.(visibleSearchFields[0].id);
    }
  }, [searchField, visibleSearchFields, onSearchFieldChange]);

  // Treat "any active filter" as: caller-provided hasActiveFilters (preferred),
  // or — as a legacy fallback — a non-empty search box. Notably NOT
  // `filterPanelOpen`, which is just UI state for the side panel's expand toggle.
  const filtersAreActive = hasActiveFilters ?? !!searchText;

  const emptyStateMessage = (() => {
    if (searchText && isNonDefaultScope && selectedSearchField) {
      return `No items match "${searchText}" in ${selectedSearchField.label}. Try a different field or clear filters.`;
    }
    if (filtersAreActive) {
      return 'No items match your current filters or search criteria.';
    }
    return 'No items to display.';
  })();

  const searchFieldMenuItems = useMemo(() => {
    if (!showSearchFieldDropdown) return undefined;
    return visibleSearchFields!.map((opt, idx) => ({
      id: idx,
      name: opt.label,
      onMenuItemClick: () => handleSelectSearchField(opt.id),
    }));
  }, [showSearchFieldDropdown, visibleSearchFields, handleSelectSearchField]);

  // NOTE: `selectedSearchField` is undefined when fewer than 2 search fields
  // are available (the dropdown is hidden). The hook must still run every
  // render to satisfy rules-of-hooks, so we feed it an empty label and gate
  // the actual render below with `selectedSearchField && (...)`.
  //
  // Pass an element (not a plain string) so the label inherits the trigger
  // button's color/font (orange, 600) via `style: inherit` — Typography's
  // body1 variant would otherwise paint its own color/size. The element branch
  // of useEllipsis merges this style with its truncation rules.
  const { renderEllipsisText: renderDescription } = useEllipsis({
    text: (
      <Typography variant="body1" style={{ color: 'inherit', font: 'inherit', fontWeight: 'inherit' }}>
        {selectedSearchField?.label ?? ''}
      </Typography>
    ),
  });
  const renderTable = useCallback(
    () => (
      <>
        <Paper variant="outlined">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(searchable || filterPanel) && (
              <div style={{ backgroundColor: colors.grey10, borderBottom: `1px solid ${colors.grey30}`, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {filterPanel && (
                    <IconButton
                      style={{ color: filterPanelOpen ? colors.primary.main : undefined }}
                      className="iconButtonMedium"
                      data-testid="datatable-filter-button"
                      onClick={toggleFilterPanel}
                    >
                      <FilterIcon />
                    </IconButton>
                  )}

                  {searchable && (
                    // Cap the width on desktop but allow it to shrink on narrow
                    // (mobile) viewports — a fixed width overflowed the container.
                    <div style={{ width: '100%', maxWidth: showSearchFieldDropdown ? 480 : 320, minWidth: 0 }}>
                      {/* min-width:0 lets the input shrink below its content's intrinsic width inside the flex row */}
                      <Input
                        value={searchText}
                        onChange={handleSearchInputChange}
                        variant="outlined"
                        placeholder={
                          searchPlaceholder ?? (showSearchFieldDropdown ? 'Search term' : 'Search for items')
                        }
                        inputProps={{ 'data-testid': 'datatable-search-input' }}
                        startAdornment={
                          !searchText ? (
                            <InputAdornment position="start">
                              <SearchSharp color="primary" />
                            </InputAdornment>
                          ) : undefined
                        }
                        endAdornment={
                          searchText || showSearchFieldDropdown ? (
                            <InputAdornment position="end">
                              {searchText && (
                                <IconButton
                                  onClick={handleClearSearchInput}
                                  size="small"
                                  data-testid="datatable-search-clear-button"
                                >
                                  <Close />
                                </IconButton>
                              )}
                              {selectedSearchField && (
                                <button
                                  type="button"
                                  onClick={e => setSearchFieldMenuAnchor(e.currentTarget)}
                                  data-testid="datatable-search-field-trigger"
                                  aria-haspopup="listbox"
                                  aria-expanded={!!searchFieldMenuAnchor}
                                  style={SEARCH_FIELD_TRIGGER_STYLE}
                                >
                                  {/* Capped, shrinkable label region: the label truncates here; the chevron never shrinks/clips. */}
                                  <span style={SEARCH_FIELD_LABEL_STYLE}>{renderDescription()}</span>
                                  <ExpandMore fontSize="small" style={{ flexShrink: 0 }} />
                                </button>
                              )}
                            </InputAdornment>
                          ) : undefined
                        }
                        fullWidth
                      />
                    </div>
                  )}

                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
                    {actions && actions}
                    {!!kebabMenuItems && renderKebabMenu()}
                  </div>
                </div>

                {filterPanel && (
                  <Collapse in={filterPanelOpen} timeout="auto">
                    <div style={{ marginTop: 16 }}>{filterPanel}</div>
                  </Collapse>
                )}
              </div>
            )}

            <TableContainer style={{ overflowX: 'auto', ...styles?.tableContainer }}>
              <Table stickyHeader>
                <TableHead id="oasis-data-table-head">
                  <Row style={{ backgroundColor: colors.grey10 }}>
                    {checkboxSelection && (
                      <Cell padding="checkbox" component="th">
                        <div style={{ paddingLeft: 16, display: 'inline-block' }}>
                          <Checkbox
                            indeterminate={false}
                            checked={false}
                            onChange={handleCheckboxChange}
                            disabled={loading}
                          />
                        </div>
                      </Cell>
                    )}
                    {table.getHeaderGroups().map(headerGroup =>
                      headerGroup.headers.map((header, index) => (
                        <Cell
                          key={`header_${index}`}
                          component="th"
                          className={header.column.columnDef.meta?.classes?.columnCell}
                          style={{
                            width: header.getSize(),
                            maxWidth: header.column.columnDef.maxSize,
                            ...header.column.columnDef.meta?.columnStyle?.columnCell,
                          }}
                        >
                          <div
                            className={header.column.columnDef.meta?.classes?.columnContainer}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: header.column.getCanSort() ? 'pointer' : 'auto',
                              ...header.column.columnDef.meta?.columnStyle?.columnContainer,
                            }}
                            onClick={e => {
                              if (header.column.getCanSort()) {
                                const handler = header.column.getToggleSortingHandler();
                                if (handler) {
                                  handler(e);
                                }
                              }
                            }}
                          >
                            <div
                              className={header.column.columnDef.meta?.classes?.columnTitle}
                              style={{
                                cursor: header.column.getCanSort() ? 'pointer' : 'auto',
                                ...header.column.columnDef.meta?.columnStyle?.columnTitle,
                              }}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>

                            {header.column.getCanSort() ? (
                              <SortLabel
                                active={!!header.column.getIsSorted()}
                                direction={
                                  header.column.getIsSorted() === 'asc'
                                    ? 'asc'
                                    : header.column.getIsSorted() === 'desc'
                                      ? 'desc'
                                      : undefined
                                }
                                IconComponent={header.column.columnDef.meta?.sortLabel ?? ExpandMore}
                                disabled={loading}
                                className={header.column.columnDef.meta?.classes?.columnSortLabelContainer}
                                style={{ ...header.column.columnDef.meta?.columnStyle?.columnSortLabelContainer }}
                              />
                            ) : null}
                          </div>
                        </Cell>
                      )),
                    )}
                  </Row>
                </TableHead>

                <TableBody>
                  {!loading && table.getRowModel().rows.length === 0 && (
                    <Row>
                      <Cell
                        colSpan={allColumns.length + (checkboxSelection ? 1 : 0)}
                        align="center"
                        style={{ padding: '48px 16px' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            color: colors.grey140,
                          }}
                        >
                          <EmptyBoxIcon fontSize={138} />
                          <Typography variant="h4" align="center" color="inherit" style={{ marginTop: 16 }}>
                            No Results Found
                          </Typography>
                          <Typography variant="body1" align="center" color="inherit" style={{ marginTop: 16 }}>
                            {emptyStateMessage}
                          </Typography>
                          {filtersAreActive && onClearFilters && (
                            <Button
                              onClick={() => {
                                onClearSearchInput?.();
                                onClearFilters?.();
                              }}
                              color="primary"
                              size="small"
                              style={{ marginTop: 16 }}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </div>
                      </Cell>
                    </Row>
                  )}
                  {!loading &&
                    table.getRowModel().rows.length > 0 &&
                    table.getRowModel().rows.map((row, rowIndex) => {
                      const cells = (
                        <>
                          {checkboxSelection && row.original && (
                            <Cell padding="checkbox">
                              <div style={{ paddingLeft: 16, display: 'inline-block' }}>
                                <Checkbox checked={false} onChange={handleCheckboxChange} disabled={loading} />
                              </div>
                            </Cell>
                          )}
                          {row.getVisibleCells().map(cell => (
                            <Cell
                              key={cell.id}
                              component="td"
                              scope="row"
                              className={cell.column.columnDef.meta?.classes?.rowCell}
                              style={{
                                width: cell.column.getSize(),
                                maxWidth: cell.column.columnDef.maxSize,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                ...cell.column.columnDef.meta?.columnStyle?.rowCell,
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Cell>
                          ))}
                        </>
                      );

                      const defaultRow = (
                        <Row
                          key={row.id}
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          onClick={() => handleRowClick(row.original)}
                          style={rowStyle(row.original, rowIndex)}
                        >
                          {cells}
                        </Row>
                      );

                      if (RowComponent) {
                        return (
                          <RowComponent key={`row_${rowIndex}`} row={row}>
                            {defaultRow}
                          </RowComponent>
                        );
                      }

                      return defaultRow;
                    })}
                  {loading &&
                    Array.from(Array(table.getState().pagination.pageSize).keys()).map((_el, index) => (
                      <Row hover role="checkbox" tabIndex={-1} key={`$load_row_${index}`}>
                        {table
                          .getAllColumns()
                          .filter(column => column.getIsVisible())
                          .map((_column, cellIndex) => (
                            <Cell key={`load_cell_${cellIndex}`} component="td" scope="row">
                              <Skeleton variant="text" animation="wave" />
                            </Cell>
                          ))}
                      </Row>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!hidePagination && (
              <Table>
                <TableFooter style={styles?.tableFooter}>{renderPagination()}</TableFooter>
              </Table>
            )}
          </div>
        </Paper>
        {showSearchFieldDropdown && (
          <NewDropdownMenu
            open={!!searchFieldMenuAnchor}
            anchorEl={searchFieldMenuAnchor}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            // Nudge 8px right so the menu aligns with the trigger's visual edge
            // rather than its bounding box (the trigger has 4px right padding).
            PaperProps={{ style: { marginLeft: 8 } }}
            menuItems={searchFieldMenuItems}
            onClose={() => setSearchFieldMenuAnchor(null)}
          />
        )}
      </>
    ),
    [
      searchable,
      filterPanel,
      filterPanelOpen,
      toggleFilterPanel,
      showSearchFieldDropdown,
      searchText,
      handleSearchInputChange,
      searchPlaceholder,
      handleClearSearchInput,
      selectedSearchField,
      searchFieldMenuAnchor,
      renderDescription,
      actions,
      kebabMenuItems,
      renderKebabMenu,
      styles?.tableContainer,
      styles?.tableFooter,
      checkboxSelection,
      handleCheckboxChange,
      loading,
      table,
      allColumns.length,
      emptyStateMessage,
      filtersAreActive,
      onClearFilters,
      hidePagination,
      renderPagination,
      searchFieldMenuItems,
      onClearSearchInput,
      rowStyle,
      RowComponent,
      handleRowClick,
    ],
  );

  return {
    table,
    renderTable,
    handleOpenColumnVisibility,
    exportExcel,
    exportCsv,
    exportPdf,
    filterPanelOpen,
    toggleFilterPanel,
  };
}
